<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import CSelect, { valueReducer } from '@/components/shared/CSelect';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import { Quality, Size, Background } from '@/typings/customilyElements/imageToImageType';

const DEFAULT_CONFIG = {
    quality: Quality.HIGH,
    size: Size.AUTO,
    background: Background.AUTO,
    stream: false
} as const;

const CONFIG_OPTIONS = {
    quality: [
        { label: 'High (default)', value: Quality.HIGH },
        { label: 'Medium', value: Quality.MEDIUM },
        { label: 'Low', value: Quality.LOW },
        { label: 'Auto', value: Quality.AUTO }
    ],
    size: [
        { label: '1024x1024 (square)', value: Size.SQUARE },
        { label: '1536x1024 (landscape)', value: Size.LANDSCAPE },
        { label: '1024x1536 (portrait)', value: Size.PORTRAIT },
        { label: 'Auto (default)', value: Size.AUTO }
    ],
    background: [
        { label: 'Transparent', value: Background.TRANSPARENT },
        { label: 'Opaque', value: Background.OPAQUE },
        { label: 'Auto (default)', value: Background.AUTO }
    ]
};

const props = defineProps<{
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
    readonly?: boolean;
}>();

const model = reactive({
    quality: props.imageElement?.imageToImageConfig?.quality || DEFAULT_CONFIG.quality,
    size: props.imageElement?.imageToImageConfig?.size || DEFAULT_CONFIG.size,
    background: props.imageElement?.imageToImageConfig?.background || DEFAULT_CONFIG.background,
    stream: props.imageElement?.imageToImageConfig?.stream || DEFAULT_CONFIG.stream
});

const isDisabled = computed(() => !!props.imageElement?.locked);

const updateImageToImageConfig = () => {
    if (!props.imageElement || isDisabled.value) return;

    const newConfig = {
        ...props.imageElement.imageToImageConfig,
        quality: model.quality,
        size: model.size,
        background: model.background,
        stream: model.stream
    };

    props.imageElement.set('imageToImageConfig', newConfig);

    if (props.editMode) {
        props.imageElement.setEditedProp('imageToImageConfig');
    }

    props.canvas.requestRenderAll();
};

watch(
    () => props.imageElement,
    () => {
        const newConfig = props.imageElement?.imageToImageConfig;
        model.quality = newConfig?.quality || DEFAULT_CONFIG.quality;
        model.size = newConfig?.size || DEFAULT_CONFIG.size;
        model.background = newConfig?.background || DEFAULT_CONFIG.background;
        updateImageToImageConfig();
    },
    { immediate: true }
);
</script>

<template>
    <div class="dynamic-vector-properties-wrapper">
        <c-select
            v-model="model.quality"
            class="mb-2"
            :options="CONFIG_OPTIONS.quality"
            label="Quality"
            :model-transform="valueReducer"
            :disabled="isDisabled"
            @input="updateImageToImageConfig"
        />
        <c-select
            v-model="model.size"
            class="mb-2"
            :options="CONFIG_OPTIONS.size"
            label="Size"
            :model-transform="valueReducer"
            :disabled="isDisabled"
            @input="updateImageToImageConfig"
        />
        <c-select
            v-model="model.background"
            class="mb-2"
            :options="CONFIG_OPTIONS.background"
            label="Background"
            :model-transform="valueReducer"
            :disabled="isDisabled"
            @input="updateImageToImageConfig"
        />
        <c-checkbox
            v-model="model.stream"
            class="mt-4 mb-2"
            label="Stream partial images (may incur extra tokens) - experimental"
            :disabled="isDisabled"
            @input="updateImageToImageConfig"
        />
    </div>
</template>
